/**
 * Lifelines Trackit T4A (Iss 2.2 Appendix 5) default channel-map profile.
 * Maps generic acquisition labels (ChN-Ref) onto clinical 10-20 names without
 * rewriting parquet / Arrow column names.
 */

export const LIFELINES_T4A_DEFAULT_PROFILE_ID = "lifelines-t4a-default";
export const LIFELINES_T4A_DEFAULT_PROFILE_VERSION = 1;

/** Acquisition → clinical aliases for Ch1–Ch22 only (Ch23–28 / 29A–32B excluded). */
export const LIFELINES_T4A_DEFAULT_ALIASES: Readonly<Record<string, string>> = {
  "Ch1-Ref": "Fp1",
  "Ch2-Ref": "Fp2",
  "Ch3-Ref": "F3",
  "Ch4-Ref": "F4",
  "Ch5-Ref": "C3",
  "Ch6-Ref": "C4",
  "Ch7-Ref": "P3",
  "Ch8-Ref": "P4",
  "Ch9-Ref": "O1",
  "Ch10-Ref": "O2",
  "Ch11-Ref": "F7",
  "Ch12-Ref": "F8",
  "Ch13-Ref": "T3",
  "Ch14-Ref": "T4",
  "Ch15-Ref": "T5",
  "Ch16-Ref": "T6",
  "Ch17-Ref": "A1",
  "Ch18-Ref": "A2",
  "Ch19-Ref": "Fz",
  "Ch20-Ref": "Cz",
  "Ch21-Ref": "Pz",
  "Ch22-Ref": "Oz",
};

export type ChannelMapConfidence = "high" | "suggested";

export type ChannelMapProfile = {
  id: string;
  version: number;
  displayName: string;
  aliases: Readonly<Record<string, string>>;
};

export type ResolvedChannelMapProfile = ChannelMapProfile & {
  confidence: ChannelMapConfidence;
};

export type ChannelMapAliasMaps = {
  acquisitionToClinical: ReadonlyMap<string, string>;
  clinicalToAcquisition: ReadonlyMap<string, string>;
};

export const LIFELINES_T4A_DEFAULT_PROFILE: ChannelMapProfile = {
  id: LIFELINES_T4A_DEFAULT_PROFILE_ID,
  version: LIFELINES_T4A_DEFAULT_PROFILE_VERSION,
  displayName: "Lifelines T4A default",
  aliases: LIFELINES_T4A_DEFAULT_ALIASES,
};

const PROFILES_BY_ID: ReadonlyMap<string, ChannelMapProfile> = new Map([
  [LIFELINES_T4A_DEFAULT_PROFILE_ID, LIFELINES_T4A_DEFAULT_PROFILE],
]);

const TRACKIT_DEVICE_RE = /trackit|lifelines|t4a/i;
const CH_N_RE = /^CH(\d+)(?:-REF)?$/i;
const CH_N_REF_RE = /^CH(\d+)-REF$/i;
const AUX_PAIR_RE = /^(29|30|31|32)A-\1B$/i;

function stripModalityPrefix(name: string): string {
  return name.trim().toUpperCase().replace(/^(EEG|ECG|EMG|EOG)\s+/, "");
}

/** Normalize acquisition keys so Ch1 and Ch1-Ref share a lookup token. */
export function normalizeAcquisitionChannelKey(name: string): string {
  const bare = stripModalityPrefix(name);
  const match = bare.match(CH_N_RE);
  if (match) {
    return `CH${Number(match[1])}`;
  }
  return bare.replace(/[^A-Z0-9]+/g, "");
}

/** Clinical electrode token: uppercased, referential suffixes stripped. */
export function normalizeClinicalElectrodeToken(name: string): string {
  let token = stripModalityPrefix(name);
  if (token.endsWith("-REF")) {
    token = token.slice(0, -4);
  }
  return token.replace(/[^A-Z0-9]+/g, "");
}

export function getChannelMapProfileById(
  profileId: string | null | undefined,
): ChannelMapProfile | null {
  if (!profileId?.trim()) return null;
  return PROFILES_BY_ID.get(profileId.trim()) || null;
}

export function detectLifelinesTrackitDevice(
  edfDevice?: string | null,
  recordingIdFromEdf?: string | null,
): boolean {
  const haystack = [edfDevice, recordingIdFromEdf]
    .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
    .join(" ");
  return haystack.length > 0 && TRACKIT_DEVICE_RE.test(haystack);
}

/**
 * True when the recording looks like a Lifelines T4A generic ChN layout:
 * ≥20 of Ch1–Ch28 (with or without -Ref) AND (any 29A-29B…32A-32B OR ≥22 ChN-Ref labels).
 */
export function detectLifelinesT4aGenericLayout(
  channelNames: string[],
): boolean {
  if (!Array.isArray(channelNames) || channelNames.length === 0) {
    return false;
  }

  const presentCh1To28 = new Set<number>();
  let chNRefStyleCount = 0;
  let hasAuxPair = false;

  for (const raw of channelNames) {
    if (typeof raw !== "string" || !raw.trim()) continue;
    const bare = stripModalityPrefix(raw);

    if (AUX_PAIR_RE.test(bare)) {
      hasAuxPair = true;
    }

    const refMatch = bare.match(CH_N_REF_RE);
    if (refMatch) {
      chNRefStyleCount += 1;
      const n = Number(refMatch[1]);
      if (n >= 1 && n <= 28) presentCh1To28.add(n);
      continue;
    }

    const plainMatch = bare.match(/^CH(\d+)$/i);
    if (plainMatch) {
      const n = Number(plainMatch[1]);
      if (n >= 1 && n <= 28) presentCh1To28.add(n);
    }
  }

  return (
    presentCh1To28.size >= 20 && (hasAuxPair || chNRefStyleCount >= 22)
  );
}

export function buildAliasMaps(
  profile: Pick<ChannelMapProfile, "aliases">,
): ChannelMapAliasMaps {
  const acquisitionToClinical = new Map<string, string>();
  const clinicalToAcquisition = new Map<string, string>();

  for (const [acquisition, clinical] of Object.entries(profile.aliases)) {
    if (!acquisition?.trim() || !clinical?.trim()) continue;
    const acqKey = normalizeAcquisitionChannelKey(acquisition);
    const clinicalToken = normalizeClinicalElectrodeToken(clinical);
    if (!acqKey || !clinicalToken) continue;
    acquisitionToClinical.set(acqKey, clinical.trim());
    // Prefer the profile's canonical acquisition spelling (usually ChN-Ref).
    if (!clinicalToAcquisition.has(clinicalToken)) {
      clinicalToAcquisition.set(clinicalToken, acquisition.trim());
    }
  }

  return { acquisitionToClinical, clinicalToAcquisition };
}

export function buildAliasMapsFromSnapshot(
  aliases: Record<string, string> | null | undefined,
): ChannelMapAliasMaps | null {
  if (!aliases || typeof aliases !== "object" || Array.isArray(aliases)) {
    return null;
  }
  const entries = Object.entries(aliases).filter(
    ([from, to]) =>
      typeof from === "string" &&
      typeof to === "string" &&
      from.trim().length > 0 &&
      to.trim().length > 0,
  );
  if (entries.length === 0) return null;
  return buildAliasMaps({ aliases: Object.fromEntries(entries) });
}

export function resolveChannelMapProfile(input: {
  channelNames: string[];
  edfDevice?: string | null;
  recordingIdFromEdf?: string | null;
  explicitProfileId?: string | null;
}): ResolvedChannelMapProfile | null {
  const explicit = getChannelMapProfileById(input.explicitProfileId);
  if (explicit) {
    return { ...explicit, confidence: "high" };
  }

  if (!detectLifelinesT4aGenericLayout(input.channelNames)) {
    return null;
  }

  const deviceMatch = detectLifelinesTrackitDevice(
    input.edfDevice,
    input.recordingIdFromEdf,
  );

  return {
    ...LIFELINES_T4A_DEFAULT_PROFILE,
    // Layout-only (no device string) is still returned as "suggested" so staging
    // Trackit BDFs without edf_device can auto-attach; UI confirm can clear later.
    confidence: deviceMatch ? "high" : "suggested",
  };
}

/** Prisma-ready fields for ingest auto-attach (high + suggested). */
export function buildChannelMapPersistFields(input: {
  channelNames: string[];
  edfDevice?: string | null;
  recordingIdFromEdf?: string | null;
  explicitProfileId?: string | null;
}): {
  channel_map_profile_id: string;
  channel_map_profile_version: number;
  channel_map_aliases: Record<string, string>;
} | null {
  const resolved = resolveChannelMapProfile(input);
  if (!resolved) return null;
  return {
    channel_map_profile_id: resolved.id,
    channel_map_profile_version: resolved.version,
    channel_map_aliases: { ...resolved.aliases },
  };
}
